import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { authClient, useSession } from "@/lib/auth/client";

type Status = "loading" | "ready" | "accepting" | "done" | "error";

type InvitePreview = {
  email: string;
  role: string | null;
  organizationName: string;
  inviterName: string | null;
  inviterEmail: string | null;
  accountExists: boolean;
};

export function AcceptInvitePage() {
  const search = useSearch({ from: "/accept-invite" }) as { token?: string };
  const navigate = useNavigate();
  const { data: session, isPending: sessionLoading } = useSession();

  const [status, setStatus] = useState<Status>("loading");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const hasAutoAccepted = useRef(false);

  useEffect(() => {
    if (!search.token) {
      setStatus("error");
      setErrorMsg("Missing invitation token");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `/api/invitations/preview?token=${encodeURIComponent(search.token!)}`,
        );
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `Could not load invitation (${res.status})`);
        }
        const data = (await res.json()) as InvitePreview;
        if (!cancelled) {
          setPreview(data);
          setStatus("ready");
        }
      } catch (err) {
        if (!cancelled) {
          setStatus("error");
          setErrorMsg(err instanceof Error ? err.message : "Could not load invitation");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [search.token]);

  const accept = useCallback(async () => {
    if (!search.token) return;
    setStatus("accepting");
    try {
      const client = authClient as unknown as {
        organization: {
          acceptInvitation: (args: {
            invitationId: string;
          }) => Promise<{ error?: { message?: string } }>;
        };
      };
      const result = await client.organization.acceptInvitation({ invitationId: search.token });
      if (result?.error) throw new Error(result.error.message ?? "Could not accept invitation");
      setStatus("done");
      toast.success(`You've joined ${preview?.organizationName ?? "the workspace"}`);
      setTimeout(() => navigate({ to: "/dashboard" }), 800);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Could not accept invitation");
    }
  }, [search.token, preview, navigate]);

  // Auto-accept once preview + session are loaded and the session email matches.
  // The user explicitly arrived here to accept — making them click another button
  // is friction with no upside. Errors still surface via setStatus("error").
  useEffect(() => {
    if (hasAutoAccepted.current) return;
    if (status !== "ready") return;
    if (!preview) return;
    if (sessionLoading) return;
    if (!session?.user) return;
    if (session.user.email.toLowerCase() !== preview.email.toLowerCase()) return;
    hasAutoAccepted.current = true;
    accept();
  }, [status, preview, session, sessionLoading, accept]);

  const returnTo = search.token ? `/accept-invite?token=${search.token}` : undefined;

  return (
    <div className="mx-auto mt-24 max-w-md space-y-4 p-6 text-center">
      <h1 className="text-2xl font-semibold">Accept invitation</h1>

      {status === "loading" && <p className="text-muted-foreground">Loading invitation…</p>}

      {status === "error" && (
        <p className="text-destructive">{errorMsg ?? "Could not load invitation"}</p>
      )}

      {status === "done" && <p>Welcome aboard. Redirecting…</p>}

      {(status === "ready" || status === "accepting") && preview && (
        <>
          <p className="text-muted-foreground">
            {preview.inviterName ?? preview.inviterEmail ?? "Someone"} invited{" "}
            <strong>{preview.email}</strong> to join <strong>{preview.organizationName}</strong>.
          </p>

          {sessionLoading ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : session?.user ? (
            session.user.email.toLowerCase() === preview.email.toLowerCase() ? (
              // Auto-accept fires the moment session and preview match; this label
              // is just feedback while the request is in flight.
              <p className="text-muted-foreground">
                {status === "accepting"
                  ? `Joining ${preview.organizationName}…`
                  : "Preparing your invitation…"}
              </p>
            ) : (
              <p className="text-destructive">
                You're signed in as <strong>{session.user.email}</strong>, but this invitation was
                sent to <strong>{preview.email}</strong>. Sign out and sign in with the invited
                address to accept.
              </p>
            )
          ) : preview.accountExists ? (
            <Button asChild>
              <Link
                to="/login"
                search={{
                  next: returnTo,
                  email: preview.email,
                }}
              >
                Sign in to accept
              </Link>
            </Button>
          ) : (
            <Button asChild>
              <Link
                to="/signup"
                search={{
                  next: returnTo,
                  email: preview.email,
                }}
              >
                Create account & join
              </Link>
            </Button>
          )}
        </>
      )}
    </div>
  );
}
